<script lang="ts">
    import translate from '../../../../lib/translate'
    import {onInput} from '../../../../lib/forms'
    import {createEventDispatcher} from 'svelte'
    import {writable} from 'svelte/store'
    import {fade} from 'svelte/transition'
    import type {ILabel} from '../../../../types'    
    import {notificationsStore, RegisterResult, sessionStore} from '../../stores'
    import {NotificationType} from '../../types/Bids'
    import label from './Register.translation'

    const dispatch = createEventDispatcher()
    const closePanel = () => {
        dispatch('closePanel')
    }
    const openCompleteDataPanel = () => {
        dispatch('openCompleteDataPanel')
    }

    let isProcessing = writable(false)
    let form = {
        invalidLabel: <ILabel|null>null,
        gender: {
            value: globalThis.config.registerGenders[0].value
        },
        title: {
            value: globalThis.config.registerTitles[0].value
        },
        firstName: {
            value: '', 
            invalidLabel: <ILabel|null>null
        },
        lastName: {
            value: '', 
            invalidLabel: <ILabel|null>null
        },
        street: {
            value: '', 
            invalidLabel: <ILabel|null>null
        },
        postalCode: {
            value: '', 
            invalidLabel: <ILabel|null>null
        },
        city: {
            value: '', 
            invalidLabel: <ILabel|null>null
        },
        country: {
            value: globalThis.config.registerCountries[0].countries[0].value
        },
        email: {
            value: '', 
            invalidLabel: <ILabel|null>null
        },
        passwordsHidden: true,
        password: {
            value: '',
            invalidLabel: <ILabel|null>null
        },
        passwordConfirmation: {
            value: '',
            invalidLabel: <ILabel|null>null
        },
    }
    
    const validateForm = () => {
        form.firstName.invalidLabel = form.firstName.value === '' ? label.invalidEmpty : null
        form.lastName.invalidLabel = form.lastName.value === '' ? label.invalidEmpty : null
        form.street.invalidLabel = form.street.value === '' ? label.invalidEmpty : null
        form.postalCode.invalidLabel = form.postalCode.value === '' ? label.invalidEmpty : null
        form.city.invalidLabel = form.city.value === '' ? label.invalidEmpty : null
        form.email.invalidLabel = form.email.value === '' ? label.invalidEmpty : null
        form.password.invalidLabel = form.password.value === '' ? label.invalidEmpty : null
        form.passwordConfirmation.invalidLabel = form.passwordConfirmation.value === '' ? label.invalidEmpty : null

        if( !form.password.invalidLabel &&
            form.password.value.length < 6
        ) {
            form.password.invalidLabel = label.invalidPasswordTooShort
        }
        if( !form.passwordConfirmation.invalidLabel &&
            form.passwordConfirmation.value !== form.password.value
        ) {
            form.passwordConfirmation.invalidLabel = label.invalidPasswordConfirmationNotEqual
        }    
    }
    
    const submitForm = () => {
        
        // client side form validation
        
        validateForm()
        
        if( form.firstName.invalidLabel ||
            form.lastName.invalidLabel ||
            form.street.invalidLabel ||
            form.postalCode.invalidLabel ||
            form.city.invalidLabel ||
            form.email.invalidLabel ||
            form.password.invalidLabel ||
            form.passwordConfirmation.invalidLabel
        ) {
            return
        }
        
        isProcessing.set(true)
        
        // send data

        sessionStore.register(
            form.gender.value, 
            form.title.value, 
            form.firstName.value,
            form.lastName.value,
            form.street.value,
            form.postalCode.value,
            form.city.value,
            form.country.value,
            form.email.value,
            form.password.value,
            form.passwordConfirmation.value
        )
            .then(result => {
                if(result === RegisterResult.success) {
                    notificationsStore.add({
                        title: translate(label.registerSuccessTitle),
                        type: NotificationType.confirmation,
                        message: translate(label.registerSuccessText),
                        durationMs: 5000,
                    })
                    openCompleteDataPanel()
                    return
                }
                if(result === RegisterResult.invalidEmailAlreadyInUse) {
                    form.email.invalidLabel = label.invalidEmailAlreadyInUse
                    return
                }
                if(result === RegisterResult.invalidEmailSyntax) {
                    form.email.invalidLabel = label.invalidEmailAddress
                    return
                }
                if(result === RegisterResult.invalidPasswordTooShort) {
                    form.password.invalidLabel = label.invalidPasswordTooShort
                    return
                }
                if(result === RegisterResult.invalidPasswordsNotEqual) {
                    form.passwordConfirmation.invalidLabel = label.invalidPasswordConfirmationNotEqual
                    return
                }
                if(result === RegisterResult.invalidOther) {
                    validateForm()
                    return
                }
                if(result === RegisterResult.error) {
                    form.invalidLabel = label.serverError
                    return
                }
                if(result === RegisterResult.invalidToken) {
                    document.location.reload()
                    return
                }
            })
            .finally(() => {isProcessing.set(false)})
    }
</script>

<div class="auction-agent-display-registration auction-agent-form">
    <span class="close" on:click={closePanel}></span>
    
    <h3>{translate(label.register)}</h3>
    
    <form action="#" 
          class="dynamic-label agent-form" 
          id="agent-registration"
          on:submit|preventDefault|stopPropagation={submitForm}
          novalidate>

        {#if form.invalidLabel}
            <div class="form-error-message">
                <p>{translate(form.invalidLabel)}</p>
            </div>
        {/if}

        <div class="input-box input-box-select select-box select-box-gender input-box-left">
            <select name="agent-registration-gender"
                    id="agent-registration-gender"
                    bind:value={form.gender.value}>
                {#each globalThis.config.registerGenders as gender}
                    <option value={gender.value}>{gender.label}</option>
                {/each}
            </select>
            <label for="agent-registration-title">{translate(label.gender)}</label>
        </div>

        <div class="input-box input-box-select select-box select-box-title input-box-right">
            <select name="agent-registration-title"
                    id="agent-registration-title"
                    bind:value={form.title.value}>
                {#each globalThis.config.registerTitles as title}
                    <option value={title.value}>{title.label}</option>
                {/each}
            </select>
            <label for="agent-registration-title">{translate(label.title)}</label>
        </div>
        
        <div class="input-box input-box-left">
            <input type="text" 
                   placeholder=" " 
                   name="agent-registration-firstname" 
                   id="agent-registration-firstname"
                   bind:value={form.firstName.value}
                   on:change={() => { onInput(form.firstName) }}
                   on:keyup={() => { onInput(form.firstName) }}>
            <label for="agent-registration-firstname">{translate(label.firstName)}</label>
            {#if form.firstName.invalidLabel}
                <p class="validation-advice validation-advice-firstname">{translate(form.firstName.invalidLabel)}</p>
            {/if}
        </div>
        
        <div class="input-box input-box-right">
            <input type="text" 
                   placeholder=" " 
                   name="agent-registration-lastname" 
                   id="agent-registration-lastname"
                   bind:value={form.lastName.value}
                   on:change={() => { onInput(form.lastName) }}
                   on:keyup={() => { onInput(form.lastName) }}>
            <label for="agent-registration-lastname">{translate(label.lastName)}</label>
            {#if form.lastName.invalidLabel}
                <p class="validation-advice validation-advice-lastname">{translate(form.lastName.invalidLabel)}</p>
            {/if}
        </div>

        <hr>
        <div class="input-box input-box-wide">
            <input type="text" 
                   placeholder=" " 
                   name="agent-registration-street" 
                   id="agent-registration-street"
                   bind:value={form.street.value}
                   on:change={() => { onInput(form.street) }}
                   on:keyup={() => { onInput(form.street) }}>
            <label for="agent-registration-street">{translate(label.street)}</label>
            {#if form.street.invalidLabel}
                <p class="validation-advice validation-advice-street">{translate(form.street.invalidLabel)}</p>
            {/if}
        </div>
        
        <div class="input-box input-box-left input-box-zip">
            <input type="text" 
                   placeholder=" " 
                   name="agent-registration-zip"
                   id="agent-registration-zip"
                   bind:value={form.postalCode.value}
                   on:change={() => { onInput(form.postalCode) }}
                   on:keyup={() => { onInput(form.postalCode) }}>
            <label for="agent-registration-zip">{translate(label.postalCode)}</label>
            {#if form.postalCode.invalidLabel}
                <p class="validation-advice validation-advice-zip">{translate(form.postalCode.invalidLabel)}</p>
            {/if}
        </div>
        
        <div class="input-box input-box-right input-box-city">
            <input type="text" 
                   placeholder=" " 
                   name="agent-registration-city" 
                   id="agent-registration-city"
                   bind:value={form.city.value}
                   on:change={() => { onInput(form.city) }}
                   on:keyup={() => { onInput(form.city) }}>
            <label for="agent-registration-city">{translate(label.city)}</label>
            {#if form.city.invalidLabel}
                <p class="validation-advice validation-advice-city">{translate(form.city.invalidLabel)}</p>
            {/if}
        </div>
        
        <div class="input-box input-box-select select-box input-box-wide">
            <select name="agent-registration-country" 
                    id="agent-registration-country"
                    bind:value={form.country.value}>
                {#if globalThis.config.registerCountries.length === 1}
                    {#each globalThis.config.registerCountries[0].countries as country}
                        <option value="{country.value}">{country.label}</option>
                    {/each}
                {:else}
                    {#each globalThis.config.registerCountries as group}
                        <optgroup label="{group.groupLabel}">
                            {#each group.countries as country}
                                <option value="{country.value}">{country.label}</option>
                            {/each}
                        </optgroup>
                    {/each}
                {/if}
            </select>
            <label for="agent-registration-country">{translate(label.country)}</label>
        </div>
        <hr>
        <div class="input-box input-box-wide">
            <input type="email" 
                   placeholder=" " 
                   name="agent-registration-email" 
                   id="agent-registration-email" 
                   bind:value={form.email.value}
                   on:change={() => { onInput(form.email) }}
                   on:keyup={() => { onInput(form.email) }}>
            <label for="agent-registration-email">{translate(label.email)}</label>
            {#if form.email.invalidLabel}
                <p class="validation-advice validation-advice-email">{translate(form.email.invalidLabel)}</p>
            {/if}
        </div>
        
        <div class="clear"></div>
        
        <div class="input-box input-box-left">
            {#if form.passwordsHidden}
                <input type="password" 
                       placeholder=" " 
                       name="agent-registration-password" 
                       id="agent-registration-password"
                       bind:value={form.password.value}
                       on:change={() => onInput(form.password)}
                       on:keyup={() => onInput(form.password)}>
            {:else}
                <input type="text" 
                       placeholder=" " 
                       name="agent-registration-password" 
                       id="agent-registration-password"
                       bind:value={form.password.value}
                       on:change={() => onInput(form.password)}
                       on:keyup={() => onInput(form.password)}>
            {/if}
            <label for="agent-registration-password">{translate(label.password)}</label>
            <span class="toggle-password {form.passwordsHidden ? '' : 'hide'}" on:click={() => form.passwordsHidden=!form.passwordsHidden}></span>
            {#if form.password.invalidLabel}
                <p class="validation-advice validation-advice-password">{translate(form.password.invalidLabel)}</p>
            {/if}
        </div>
        
        <div class="input-box input-box-right">
            {#if form.passwordsHidden}
                <input type="password"
                       placeholder=" "
                       name="agent-registration-confirm" 
                       id="agent-registration-confirm"
                       bind:value={form.passwordConfirmation.value}
                       on:change={() => onInput(form.passwordConfirmation)}
                       on:keyup={() => onInput(form.passwordConfirmation)}>
            {:else}
                <input type="text"
                       placeholder=" "
                       name="agent-registration-confirm" 
                       id="agent-registration-confirm"
                       bind:value={form.passwordConfirmation.value}
                       on:change={() => onInput(form.passwordConfirmation)}
                       on:keyup={() => onInput(form.passwordConfirmation)}>
            {/if}
            <label for="agent-registration-confirm">{translate(label.confirmPassword)}</label>
            <span class="toggle-password {form.passwordsHidden ? '' : 'hide'}" on:click={() => form.passwordsHidden=!form.passwordsHidden}></span>
            {#if form.passwordConfirmation.invalidLabel}
                <p class="validation-advice validation-advice-confirm">{translate(form.passwordConfirmation.invalidLabel)}</p>
            {/if}
        </div>

        <hr>
        <button class="auctions-agent-registration-submit" 
                type="submit"
        >{translate(label.register)}</button>
        
        <p class="data-privacy-text">{@html translate(label.dataPrivacyAppliesHtml)}</p>
    </form>
    {#if $isProcessing}
        <div class="form-layover layover" out:fade></div>
    {/if}
</div>

